import { Link } from 'react-router-dom';
import { 
  Mail, Columns, ExternalLink, Shield, Lock, Server, ArrowRight, 
  Check, Sparkles, Zap, Globe, Clock, FolderTree, Tag, 
  Grip, Eye, Bell, CheckSquare, ListTodo, Filter, Palette,
  Smartphone, Monitor, ChevronDown, Star, Play
} from 'lucide-react';
import Logo from '../components/shared/Logo';
import { useState, useEffect } from 'react';

// Animated counter for stats
function AnimatedCounter({ end, suffix = '' }: { end: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [end]);
  
  return <span>{count.toLocaleString()}{suffix}</span>;
}

// Mock Kanban Board Visual
function KanbanMockup() {
  const columns = [
    { 
      name: 'Posteingang', 
      color: 'from-blue-500 to-blue-600',
      emails: [
        { from: 'Max Müller', subject: 'Projektupdate Q1', time: '09:15', unread: true, category: 'Arbeit' },
        { from: 'Amazon', subject: 'Ihre Bestellung wurde...', time: '08:42', category: 'Shopping' },
        { from: 'Team Lead', subject: 'Meeting morgen', time: '08:30', unread: true, category: 'Arbeit' },
      ]
    },
    { 
      name: 'In Bearbeitung', 
      color: 'from-amber-500 to-orange-500',
      emails: [
        { from: 'Kunde GmbH', subject: 'Angebot #2847', time: 'Gestern', category: 'Kunden' },
        { from: 'HR', subject: 'Urlaubsantrag', time: 'Gestern', category: 'Intern' },
      ]
    },
    { 
      name: 'Warte auf Antwort', 
      color: 'from-violet-500 to-purple-600',
      emails: [
        { from: 'Partner AG', subject: 'Vertragsentwurf', time: 'Mo', category: 'Verträge' },
      ]
    },
    { 
      name: 'Erledigt', 
      color: 'from-emerald-500 to-green-600',
      emails: [
        { from: 'Newsletter', subject: 'Wöchentlicher Digest', time: 'So', category: 'News' },
        { from: 'Support', subject: 'Ticket #1234 gelöst', time: 'Sa', category: 'Support' },
      ]
    },
  ];

  const categoryColors: Record<string, string> = {
    'Arbeit': 'bg-blue-500',
    'Shopping': 'bg-pink-500',
    'Kunden': 'bg-amber-500',
    'Intern': 'bg-cyan-500',
    'Verträge': 'bg-violet-500',
    'News': 'bg-gray-500',
    'Support': 'bg-emerald-500',
  };

  return (
    <div className="relative bg-bg-surface rounded-2xl border border-border-default overflow-hidden shadow-2xl">
      {/* Window Controls */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-default bg-bg-primary">
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="px-4 py-1 bg-bg-surface rounded-lg text-xs text-text-muted flex items-center gap-2">
            <Lock className="w-3 h-3" />
            kolumi.onsafe.app
          </div>
        </div>
      </div>
      
      {/* App Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default bg-bg-primary/50">
        <div className="flex items-center gap-3">
          <Logo variant="icon" className="h-6 w-6" />
          <span className="font-semibold text-text-primary">Mein E-Mail Board</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-bg-surface rounded-lg transition-colors">
            <Filter className="w-4 h-4 text-text-muted" />
          </button>
          <button className="p-2 hover:bg-bg-surface rounded-lg transition-colors">
            <Bell className="w-4 h-4 text-text-muted" />
          </button>
        </div>
      </div>
      
      {/* Kanban Board */}
      <div className="flex gap-4 p-4 overflow-x-auto">
        {columns.map((column, idx) => (
          <div key={idx} className="flex-shrink-0 w-72">
            {/* Column Header */}
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${column.color}`} />
              <span className="font-medium text-text-primary text-sm">{column.name}</span>
              <span className="ml-auto text-xs text-text-muted bg-bg-surface px-2 py-0.5 rounded-full">
                {column.emails.length}
              </span>
            </div>
            
            {/* Email Cards */}
            <div className="space-y-2">
              {column.emails.map((email, emailIdx) => (
                <div 
                  key={emailIdx}
                  className={`
                    p-3 bg-bg-primary rounded-xl border border-border-default 
                    hover:border-primary/30 transition-all cursor-pointer
                    hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5
                    ${email.unread ? 'border-l-2 border-l-primary' : ''}
                  `}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className={`text-sm ${email.unread ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
                      {email.from}
                    </span>
                    <span className="text-xs text-text-muted whitespace-nowrap">{email.time}</span>
                  </div>
                  <p className="text-xs text-text-muted truncate mb-2">{email.subject}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full text-white ${categoryColors[email.category]}`}>
                      {email.category}
                    </span>
                    {email.unread && (
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Drag Indicator Animation */}
      <div className="absolute bottom-20 left-1/3 opacity-0 animate-pulse">
        <Grip className="w-5 h-5 text-primary" />
      </div>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ 
  icon: Icon, 
  title, 
  description, 
  color = 'primary'
}: { 
  icon: React.ElementType;
  title: string;
  description: string;
  color?: string;
}) {
  const colorClasses: Record<string, { bg: string; text: string }> = {
    primary: { bg: 'bg-primary/10', text: 'text-primary' },
    violet: { bg: 'bg-violet-500/10', text: 'text-violet-500' },
    cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-500' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-500' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
    pink: { bg: 'bg-pink-500/10', text: 'text-pink-500' },
  };
  
  const { bg, text } = colorClasses[color] || colorClasses.primary;
  
  return (
    <div className="group p-6 bg-bg-surface border border-border-default rounded-2xl hover:border-primary/30 transition-all hover:shadow-xl hover:shadow-primary/5">
      <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        <Icon className={`w-6 h-6 ${text}`} />
      </div>
      <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
      <p className="text-text-muted text-sm leading-relaxed">{description}</p>
    </div>
  );
}

// Testimonial Component
function Testimonial({ quote, author, role, avatar }: { quote: string; author: string; role: string; avatar: string }) {
  return (
    <div className="p-6 bg-bg-surface border border-border-default rounded-2xl">
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
        ))}
      </div>
      <p className="text-text-secondary mb-4 italic">"{quote}"</p>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white font-semibold">
          {avatar}
        </div>
        <div>
          <p className="font-medium text-text-primary text-sm">{author}</p>
          <p className="text-text-muted text-xs">{role}</p>
        </div>
      </div>
    </div>
  );
}

// Pricing Card
function PricingCard({ 
  name, 
  price, 
  period,
  description, 
  features, 
  highlighted = false,
  cta = 'Jetzt starten'
}: { 
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  cta?: string;
}) {
  return (
    <div className={`
      relative p-8 rounded-2xl border transition-all
      ${highlighted 
        ? 'bg-gradient-to-b from-primary/10 to-violet-500/10 border-primary/30 shadow-xl shadow-primary/10' 
        : 'bg-bg-surface border-border-default hover:border-primary/30'
      }
    `}>
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary to-violet-500 text-white text-xs font-medium rounded-full">
          Beliebt
        </div>
      )}
      <h3 className="text-xl font-bold text-text-primary mb-2">{name}</h3>
      <p className="text-text-muted text-sm mb-4">{description}</p>
      <div className="mb-6">
        <span className="text-4xl font-bold text-text-primary">{price}</span>
        <span className="text-text-muted text-sm"> / {period}</span>
      </div>
      <ul className="space-y-3 mb-8">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3 text-sm text-text-secondary">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            {feature}
          </li>
        ))}
      </ul>
      <Link
        to="/register"
        className={`
          block w-full py-3 text-center font-medium rounded-xl transition-all
          ${highlighted 
            ? 'bg-gradient-to-r from-primary to-violet-500 text-white hover:opacity-90' 
            : 'bg-bg-primary border border-border-default text-text-primary hover:border-primary'
          }
        `}
      >
        {cta}
      </Link>
    </div>
  );
}

// FAQ Item
function FAQItem({ question, answer, defaultOpen = false }: { question: string; answer: string; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-border-default">
      <button 
        className="w-full py-5 flex items-center justify-between text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-text-primary">{question}</span>
        <ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="pb-5 text-text-muted text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Navigation */}
      <nav className="fixed top-0 inset-x-0 h-16 bg-bg-primary/80 backdrop-blur-xl border-b border-border-default z-50">
        <div className="max-w-6xl mx-auto px-4 h-full flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Features
            </a>
            <a href="#pricing" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Preise
            </a>
            <a href="#faq" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              FAQ
            </a>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Anmelden
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm bg-gradient-to-r from-primary to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Kostenlos starten
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="pt-28 pb-8 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-12">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary mb-6">
              <Sparkles className="w-4 h-4" />
              Neu: KI-gestützte Smart-Spalten
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6 leading-tight">
              E-Mail-Chaos war gestern.{' '}
              <span className="bg-gradient-to-r from-primary via-violet-500 to-pink-500 bg-clip-text text-transparent">
                Kanban ist heute.
              </span>
            </h1>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto mb-8">
              Kolumi verwandelt deinen Posteingang in ein visuelles Kanban-Board. 
              Drag & Drop statt endloses Scrollen. Für Microsoft 365 und Gmail.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link
                to="/register"
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary to-violet-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all hover:shadow-xl hover:shadow-primary/25 flex items-center justify-center gap-2"
              >
                14 Tage kostenlos testen
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#demo"
                className="w-full sm:w-auto px-8 py-4 bg-bg-surface border border-border-default text-text-primary font-semibold rounded-xl hover:border-primary/50 transition-colors flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Demo ansehen
              </a>
            </div>
            
            <p className="text-text-muted text-sm">
              Keine Kreditkarte nötig • DSGVO-konform • Server in der EU 🇪🇺
            </p>
          </div>
          
          {/* Hero Mockup */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-violet-500/20 to-pink-500/20 blur-3xl -z-10" />
            <KanbanMockup />
          </div>
        </div>
      </section>
      
      {/* Social Proof */}
      <section className="py-12 px-4 border-y border-border-default bg-bg-surface/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 text-center">
            <div>
              <div className="text-3xl font-bold text-text-primary">
                <AnimatedCounter end={2500} suffix="+" />
              </div>
              <p className="text-text-muted text-sm">Aktive Nutzer</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border-default" />
            <div>
              <div className="text-3xl font-bold text-text-primary">
                <AnimatedCounter end={150000} suffix="+" />
              </div>
              <p className="text-text-muted text-sm">Mails organisiert</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border-default" />
            <div>
              <div className="text-3xl font-bold text-text-primary">4.9</div>
              <div className="flex items-center justify-center gap-1 mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-text-muted text-sm">Nutzerbewertung</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border-default" />
            <div>
              <div className="text-3xl font-bold text-text-primary">99.9%</div>
              <p className="text-text-muted text-sm">Uptime</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Problem / Solution */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-text-primary mb-6">
                Das Problem mit E-Mail
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-500 text-xs">✕</span>
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Endlose Listen</p>
                    <p className="text-text-muted text-sm">Hunderte E-Mails untereinander. Keine Übersicht, welche wichtig sind.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-500 text-xs">✕</span>
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Status unklar</p>
                    <p className="text-text-muted text-sm">"Hab ich schon geantwortet?" "Warte ich noch auf Rückmeldung?"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-500 text-xs">✕</span>
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Zu viele Tools</p>
                    <p className="text-text-muted text-sm">E-Mail hier, Tasks dort, Notizen woanders. Chaos vorprogrammiert.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-text-primary mb-6">
                Die Kolumi-Lösung
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Visuelles Kanban</p>
                    <p className="text-text-muted text-sm">Deine E-Mails als Karten. Auf einen Blick sehen, was wo steht.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Drag & Drop Workflow</p>
                    <p className="text-text-muted text-sm">Mail bearbeitet? Einfach in "Erledigt" ziehen. Fertig.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Tasks direkt auf Mails</p>
                    <p className="text-text-muted text-sm">Checklisten und To-Dos an jede Mail anheften. Alles an einem Ort.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-bg-surface/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Alles, was du brauchst
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Mächtige Features, die deinen E-Mail-Workflow revolutionieren
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={Columns}
              title="Ordner als Spalten"
              description="Deine E-Mail-Ordner werden zu Kanban-Spalten. Inbox, Bearbeitung, Wartend, Erledigt — du bestimmst die Struktur."
              color="primary"
            />
            <FeatureCard 
              icon={FolderTree}
              title="Hierarchische Ordner"
              description="Verschachtelte Unterordner aufklappbar anzeigen. Perfekt für komplexe Strukturen."
              color="violet"
            />
            <FeatureCard 
              icon={Tag}
              title="Kategorien & Labels"
              description="Microsoft-Kategorien und Gmail-Labels als farbige Filter-Spalten nutzen."
              color="amber"
            />
            <FeatureCard 
              icon={Grip}
              title="Drag & Drop"
              description="Mails per Drag & Drop zwischen Spalten verschieben. Wird automatisch im echten Postfach synchronisiert."
              color="cyan"
            />
            <FeatureCard 
              icon={ExternalLink}
              title="Öffnen im Original"
              description="Mit einem Klick zur Mail in Outlook oder Gmail. Kolumi ist dein Dashboard, nicht dein Mail-Client."
              color="pink"
            />
            <FeatureCard 
              icon={Eye}
              title="Mail-Vorschau"
              description="Schnelle Vorschau als Sidebar oder Popup — du wählst, was dir besser gefällt."
              color="emerald"
            />
            <FeatureCard 
              icon={CheckSquare}
              title="Inline-Tasks"
              description="Checklisten direkt auf Mail-Karten erstellen. Sehe sofort, welche Mails noch Aufgaben haben."
              color="primary"
            />
            <FeatureCard 
              icon={ListTodo}
              title="Microsoft To-Do & Google Tasks"
              description="Tasks aus Mails direkt in deine To-Do-App exportieren. Mit Deep-Link zur Original-Mail."
              color="violet"
            />
            <FeatureCard 
              icon={Sparkles}
              title="KI Smart-Spalten"
              description="Lass KI automatisch Spalten erstellen basierend auf Thema, Priorität oder Absender."
              color="amber"
            />
            <FeatureCard 
              icon={Filter}
              title="Erweiterte Filter"
              description="Filtere nach Absender, Datum, Kategorie, ungelesen/gelesen und mehr."
              color="cyan"
            />
            <FeatureCard 
              icon={Palette}
              title="Anpassbares Design"
              description="Dark Mode, Light Mode, eigene Farben. Mach Kolumi zu deinem Tool."
              color="pink"
            />
            <FeatureCard 
              icon={Smartphone}
              title="Responsive Design"
              description="Funktioniert perfekt auf Desktop, Tablet und Smartphone."
              color="emerald"
            />
          </div>
        </div>
      </section>
      
      {/* Provider Logos */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-text-muted mb-8">Funktioniert mit den E-Mail-Diensten, die du bereits nutzt</p>
          <div className="flex flex-wrap items-center justify-center gap-12">
            <div className="flex items-center gap-3">
              <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#0078D4"/>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#0078D4" strokeWidth="2"/>
              </svg>
              <span className="text-text-primary font-semibold">Microsoft 365</span>
            </div>
            <div className="flex items-center gap-3">
              <svg viewBox="0 0 24 24" className="w-10 h-10">
                <path d="M20.6 6.7l-8.2 5.7c-.3.2-.7.2-1 0L3.3 6.7C3.1 6.6 3 6.3 3 6c0-.9.9-1.5 1.7-1l7.3 5 7.3-5c.8-.5 1.7.1 1.7 1 0 .3-.1.6-.4.7z" fill="#EA4335"/>
                <path d="M21 18H3V8.4l8.5 5.9c.3.2.8.2 1.1 0L21 8.4V18z" fill="#4285F4"/>
              </svg>
              <span className="text-text-primary font-semibold">Gmail</span>
            </div>
            <div className="flex items-center gap-3 text-text-muted">
              <Clock className="w-6 h-6" />
              <span className="text-sm">IMAP — bald verfügbar</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Security Section */}
      <section className="py-20 px-4 bg-bg-surface/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Datenschutz, der keine Kompromisse kennt
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Deine Daten gehören dir. Nicht uns, nicht Big Tech.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-bg-primary border border-border-default rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                Keine E-Mails gespeichert
              </h3>
              <p className="text-text-muted text-sm">
                Wir speichern niemals deine E-Mail-Inhalte. Alles wird live vom Provider geladen und nicht in unserer Datenbank abgelegt.
              </p>
            </div>
            
            <div className="p-6 bg-bg-primary border border-border-default rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                DSGVO-konform
              </h3>
              <p className="text-text-muted text-sm">
                Server in der EU. Datenverarbeitung nach europäischen Standards. Löschung auf Knopfdruck.
              </p>
            </div>
            
            <div className="p-6 bg-bg-primary border border-border-default rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-violet-500" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                AES-256 Verschlüsselung
              </h3>
              <p className="text-text-muted text-sm">
                OAuth-Tokens werden mit AES-256-GCM verschlüsselt. Selbst wir können sie nicht lesen.
              </p>
            </div>
            
            <div className="p-6 bg-bg-primary border border-border-default rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                Rate Limiting
              </h3>
              <p className="text-text-muted text-sm">
                Schutz vor Brute-Force-Angriffen durch intelligentes Rate Limiting bei Login und Registrierung.
              </p>
            </div>
            
            <div className="p-6 bg-bg-primary border border-border-default rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-cyan-500" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                HTTPS Everywhere
              </h3>
              <p className="text-text-muted text-sm">
                Alle Verbindungen sind TLS-verschlüsselt. Strenge Security-Header schützen vor Angriffen.
              </p>
            </div>
            
            <div className="p-6 bg-bg-primary border border-border-default rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-pink-500/10 flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-pink-500" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">
                Transparenz
              </h3>
              <p className="text-text-muted text-sm">
                Du siehst genau, welche Berechtigungen Kolumi anfordert. Nur das Nötigste, nichts mehr.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Was unsere Nutzer sagen
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Testimonial 
              quote="Endlich Ordnung in meinem Posteingang! Das Kanban-System ist genial für Projektmanager."
              author="Sarah K."
              role="Projektmanagerin"
              avatar="S"
            />
            <Testimonial 
              quote="Die Integration mit Microsoft To-Do ist perfekt. Ich erstelle Tasks direkt aus meinen Mails."
              author="Thomas M."
              role="Geschäftsführer"
              avatar="T"
            />
            <Testimonial 
              quote="DSGVO-konform und trotzdem modern? Kolumi zeigt, wie es geht. Endlich ein Tool für europäische Standards."
              author="Lisa B."
              role="Datenschutzbeauftragte"
              avatar="L"
            />
          </div>
        </div>
      </section>
      
      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 bg-bg-surface/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Einfache, faire Preise
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Starte kostenlos. Upgrade, wenn du bereit bist.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <PricingCard 
              name="Free"
              price="€0"
              period="für immer"
              description="Perfekt zum Ausprobieren"
              features={[
                '1 E-Mail-Konto',
                '3 Boards',
                '100 Mails pro Board',
                'Drag & Drop',
                'Kategorien & Labels',
              ]}
              cta="Kostenlos starten"
            />
            <PricingCard 
              name="Pro"
              price="€9"
              period="Monat"
              description="Für Power-User"
              features={[
                'Unbegrenzte E-Mail-Konten',
                'Unbegrenzte Boards',
                'Unbegrenzte Mails',
                'KI Smart-Spalten',
                'Microsoft To-Do & Google Tasks',
                'Erweiterte Filter',
                'Priority Support',
              ]}
              highlighted
            />
            <PricingCard 
              name="Team"
              price="€19"
              period="Nutzer/Monat"
              description="Für Teams & Unternehmen"
              features={[
                'Alles aus Pro',
                'Team-Verwaltung',
                'Shared Boards',
                'Admin-Dashboard',
                'SSO (SAML)',
                'Dedizierter Support',
                'On-Premise Option',
              ]}
              cta="Kontakt aufnehmen"
            />
          </div>
          
          <p className="text-center text-text-muted text-sm mt-8">
            Alle Preise zzgl. MwSt. Jährliche Zahlung: 2 Monate gratis.
          </p>
        </div>
      </section>
      
      {/* FAQ */}
      <section id="faq" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Häufig gestellte Fragen
            </h2>
          </div>
          
          <div className="divide-y divide-border-default border-y border-border-default">
            <FAQItem 
              question="Wie funktioniert Kolumi?"
              answer="Kolumi verbindet sich über OAuth mit deinem Microsoft 365 oder Gmail Konto. Deine E-Mails werden dann als Karten in einem Kanban-Board angezeigt. Wenn du eine Karte in eine andere Spalte ziehst, wird die Mail automatisch in den entsprechenden Ordner verschoben."
              defaultOpen
            />
            <FAQItem 
              question="Werden meine E-Mails gespeichert?"
              answer="Nein! Wir speichern niemals den Inhalt deiner E-Mails. Kolumi lädt die Mails live von deinem Provider (Microsoft/Google) und zeigt sie nur an. Die einzigen Daten, die wir speichern, sind dein Nutzerprofil und deine Board-Konfigurationen."
            />
            <FAQItem 
              question="Ist Kolumi DSGVO-konform?"
              answer="Ja. Unsere Server stehen in der EU, wir verarbeiten nur die minimal notwendigen Daten, und du kannst dein Konto jederzeit vollständig löschen. Außerdem speichern wir keine E-Mail-Inhalte — die bleiben bei Microsoft/Google."
            />
            <FAQItem 
              question="Kann ich mehrere E-Mail-Konten verbinden?"
              answer="Im Free-Plan kannst du 1 Konto verbinden. Mit Pro und Team sind unbegrenzt viele Konten möglich — ideal, wenn du berufliche und private Mails getrennt verwaltest."
            />
            <FAQItem 
              question="Funktioniert Kolumi mit meinem E-Mail-Provider?"
              answer="Aktuell unterstützen wir Microsoft 365 (Outlook, Exchange) und Gmail. IMAP-Support für andere Provider ist in Entwicklung."
            />
            <FAQItem 
              question="Was passiert nach der 14-tägigen Testphase?"
              answer="Du wirst automatisch auf den Free-Plan umgestellt. Du verlierst keine Daten — nur die Pro-Features werden eingeschränkt. Du kannst jederzeit upgraden."
            />
            <FAQItem 
              question="Gibt es eine mobile App?"
              answer="Kolumi ist als Progressive Web App (PWA) optimiert und funktioniert auf allen Geräten. Du kannst es auf dem Homescreen installieren wie eine native App. Native Apps für iOS und Android sind geplant."
            />
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-12 bg-gradient-to-br from-primary/10 via-violet-500/10 to-pink-500/10 border border-primary/20 rounded-3xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
              Bereit für den Inbox-Detox?
            </h2>
            <p className="text-text-secondary text-lg mb-8 max-w-xl mx-auto">
              Schließe dich tausenden zufriedenen Nutzern an und verwandle dein E-Mail-Chaos in produktive Ordnung.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-violet-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all hover:shadow-xl hover:shadow-primary/25"
            >
              Jetzt kostenlos starten
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-text-muted text-sm mt-4">
              14 Tage Pro-Features gratis • Keine Kreditkarte nötig
            </p>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border-default">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Logo className="mb-4" />
              <p className="text-text-muted text-sm">
                E-Mail-Management neu gedacht. Kanban für deinen Posteingang.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Produkt</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-text-muted hover:text-text-secondary transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-text-muted hover:text-text-secondary transition-colors">Preise</a></li>
                <li><a href="#faq" className="text-text-muted hover:text-text-secondary transition-colors">FAQ</a></li>
                <li><Link to="/login" className="text-text-muted hover:text-text-secondary transition-colors">Anmelden</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Rechtliches</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/impressum" className="text-text-muted hover:text-text-secondary transition-colors">Impressum</Link></li>
                <li><Link to="/datenschutz" className="text-text-muted hover:text-text-secondary transition-colors">Datenschutz</Link></li>
                <li><Link to="/agb" className="text-text-muted hover:text-text-secondary transition-colors">AGB</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Kontakt</h4>
              <ul className="space-y-2 text-sm text-text-muted">
                <li>support@kolumi.app</li>
                <li>Made in Germany 🇩🇪</li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-text-muted text-sm">
              © 2026 Kolumi. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-2">
              <span className="text-text-muted text-sm">Server in der EU</span>
              <span>🇪🇺</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
